/**
 * TranscriptLinksPanel — what was said about each item, proposed by the AI and
 * decided by a person.
 *
 * Brief K slice 2b. "Find passages" runs the transcript item linker
 * (transcriptItemLinkerService); each passage it proposes waits here until
 * someone in the session confirms it ("Yes, this is about the item") or turns
 * it down. A confirmed passage becomes a note on the item, shown in the Why
 * column and quoted in the prioritization memo. Nothing the AI proposes counts
 * before that.
 *
 * Shown only when the session has a transcript or a proposal waiting.
 */
import { useCallback, useEffect, useState } from 'react';
import { Check, Loader2, Quote, X } from 'lucide-react';
import { loadSessionTranscript } from '../../../lib/sessionTranscript';
import {
  fetchTranscriptLinkCandidates,
  reviewTranscriptLink,
  runTranscriptLinker,
  type TranscriptLinkCandidate,
} from '../../../lib/transcriptItemLinkerService';
import { useDeliberationStore } from '../../../stores/prioritization/deliberationStore';

export interface TranscriptLinksPanelProps {
  sessionId: string;
  workspaceId: string;
  /** The ranked items, in rank order. */
  items: Array<{ id: string; title: string }>;
  /** Signed in as a person (not the system user). The database decides who may act. */
  canAct: boolean;
}

export function TranscriptLinksPanel({ sessionId, workspaceId, items, canAct }: TranscriptLinksPanelProps) {
  const [hasTranscript, setHasTranscript] = useState(false);
  const [candidates, setCandidates] = useState<TranscriptLinkCandidate[]>([]);
  const [loaded, setLoaded] = useState(false);
  const [running, setRunning] = useState(false);
  const [deciding, setDeciding] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    const [transcript, waiting] = await Promise.all([loadSessionTranscript(sessionId), fetchTranscriptLinkCandidates(sessionId)]);
    setHasTranscript(transcript.text.trim().length > 0);
    setCandidates(waiting);
    setLoaded(true);
  }, [sessionId]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  if (!loaded || (!hasTranscript && candidates.length === 0)) return null;

  const titleOf = (id: string) => items.find((i) => i.id === id)?.title ?? 'An item no longer in this ranking';
  const rankOf = (id: string) => items.findIndex((i) => i.id === id);
  const ordered = [...candidates].sort((a, b) => rankOf(a.itemId) - rankOf(b.itemId));

  const find = async () => {
    setRunning(true);
    setError(null);
    setMessage(null);
    const run = await runTranscriptLinker(sessionId, workspaceId, items);
    setRunning(false);
    if (!run.ok) {
      setError(run.error ?? null);
      setMessage(run.note ?? null);
      return;
    }
    // The count of what is waiting is drawn from the list itself, below; only "nothing new" needs saying.
    setMessage(run.proposed === 0 ? 'Nothing new was found: no reason about these items in the transcript that is not already here.' : null);
    await refresh();
  };

  const decide = async (c: TranscriptLinkCandidate, decision: 'confirmed' | 'rejected') => {
    setDeciding(c.id);
    setError(null);
    const res = await reviewTranscriptLink(c.id, decision);
    setDeciding(null);
    if (!res.ok) {
      setError(res.error ?? 'That decision could not be saved.');
      return;
    }
    setCandidates((list) => list.filter((x) => x.id !== c.id));
    if (decision === 'confirmed') void useDeliberationStore.getState().fetchForSession(sessionId);
  };

  const btn =
    'inline-flex items-center gap-1.5 px-3 py-1.5 text-sm text-[var(--text-sec)] border border-[var(--rule)] hover:bg-[var(--surface)] transition-colors disabled:opacity-60';

  return (
    <section className="mt-4 border border-[var(--rule)] bg-[var(--bg-raised)] p-5" data-testid="transcript-links-panel">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="max-w-2xl">
          <h3 className="text-base font-semibold text-[var(--text-pri)]">
            What was said about each item
            <span className="ml-2 inline-flex items-center px-1.5 py-0.5 text-[11px] font-medium uppercase tracking-wide text-[var(--text-sec)] border border-[var(--rule)] align-middle">
              AI
            </span>
          </h3>
          <p className="mt-1 text-sm text-[var(--text-sec)] leading-relaxed">
            The AI finds places in the transcript where someone gave a reason about an item, and copies the words exactly.
            Nothing counts until someone here confirms it. Confirmed passages show in the Why column and are quoted in the
            memo.
          </p>
        </div>
        {canAct && hasTranscript && (
          <button type="button" onClick={() => void find()} disabled={running} className={btn} data-testid="find-transcript-passages">
            {running ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Quote className="h-3.5 w-3.5" />}
            {running ? 'Reading the transcript…' : 'Find passages'}
          </button>
        )}
      </div>

      {ordered.length > 0 && (
        <p className="mt-3 text-sm text-[var(--text-sec)]">
          {ordered.length} passage{ordered.length === 1 ? '' : 's'} to review.
        </p>
      )}
      {message && <p className="mt-3 text-sm text-[var(--text-sec)]">{message}</p>}
      {error && <p className="mt-3 text-sm text-[var(--coral)]">{error}</p>}

      {ordered.length > 0 && (
        <ul className="mt-4 divide-y divide-[var(--rule)] border-t border-[var(--rule)]">
          {ordered.map((c) => (
            <li key={c.id} className="py-3" data-testid="transcript-candidate">
              <p className="text-sm font-medium text-[var(--text-pri)]">
                {rankOf(c.itemId) >= 0 ? `#${rankOf(c.itemId) + 1} ` : ''}
                {titleOf(c.itemId)}
              </p>
              <blockquote className="mt-1 border-l-2 border-[var(--rule)] pl-3">
                <p className="text-sm text-[var(--text-pri)] leading-relaxed">“{c.excerpt}”</p>
                <footer className="mt-0.5 text-xs text-[var(--text-ter)]">
                  — From the discussion{c.transcriptTimestamp ? ` at ${c.transcriptTimestamp}` : ''}
                </footer>
              </blockquote>
              {canAct && (
                <div className="mt-2 flex flex-wrap gap-2">
                  <button type="button" onClick={() => void decide(c, 'confirmed')} disabled={deciding === c.id} className={btn}>
                    <Check className="h-3.5 w-3.5" />
                    Yes, this is about the item
                  </button>
                  <button type="button" onClick={() => void decide(c, 'rejected')} disabled={deciding === c.id} className={btn}>
                    <X className="h-3.5 w-3.5" />
                    Not about this item
                  </button>
                </div>
              )}
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

export default TranscriptLinksPanel;
